import {
  autoTitle,
  canZoom,
  displayUrl,
  guideSites,
  setZoomAll,
  stepNumbers,
  stepTitle,
  TEXT_TONE_KEYS,
  textToneLabel,
  type Locale,
  textBlock,
  type Step,
  type TextTone,
  zoomAll,
} from '@nolens/domain';
import {
  cx,
  IconButton,
  RunControls,
  SegmentedControl,
  SiteIcon,
  TONE_STYLE,
  useLocale,
} from '@nolens/ui';
import { guideUiText } from '../i18n/guide';
import { useState, type ReactNode } from 'react';

/**
 * Zoom em todas as capturas de uma vez («Com zoom» ou «Inteiras»), em vez de passo a passo.
 * Misto: nenhuma das duas escolhida. As capturas recortadas pelo autor ficam como estão.
 */
export function ZoomAll({ steps, onChange }: { steps: Step[]; onChange: (steps: Step[]) => void }) {
  const t = guideUiText(useLocale());
  const zoom = zoomAll(steps);
  if (!zoom) return null;
  const cropped = steps.filter((s) => s.imageId && s.crop).length;
  return (
    <div className="no-print mb-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-zoom-all={zoom}>
      <span className="text-sm font-medium text-text-2">{t.zoomAllLabel}</span>
      <SegmentedControl
        label={t.zoomAllLabel}
        value={zoom}
        onChange={(v) => onChange(setZoomAll(steps, v === 'on'))}
        options={[
          { value: 'on', label: t.zoomAllOn },
          { value: 'off', label: t.zoomAllOff },
        ]}
      />
      {cropped > 0 && <span className="text-[13px] text-text-3">{t.croppedKept(cropped)}</span>}
    </div>
  );
}

/**
 * Os passos de um guia, para ler (página pública, leitores) ou editar (donos e editores).
 * Na web edita-se texto, ordem, zoom (em cada passo, ou em todas com ZoomAll), a imagem
 * (destaques, setas, desfocar, recortar) e apaga-se; capturas novas só chegam pela extensão.
 */
export function GuideSteps({
  steps,
  locale,
  shot,
  onChange,
  onEditImage,
  below,
}: {
  steps: Step[];
  /** Língua do guia: títulos automáticos e nomes dos blocos de texto. */
  locale: Locale;
  shot: (step: Step, zoom: boolean) => ReactNode;
  onChange?: (steps: Step[]) => void;
  /** Abre o editor da imagem do passo (só com edição). */
  onEditImage?: (step: Step) => void;
  /** Por baixo de cada passo (ex.: comentários). */
  below?: (step: Step) => ReactNode;
}) {
  const t = guideUiText(useLocale());
  const numbers = stepNumbers({ steps });
  // Apps do Mac (ADR-G25): o ícone fica só no 1.º passo de cada app
  const apps = new Map(guideSites({ steps }).filter((s) => s.app).map((s) => [s.host, s]));
  const edit = onChange
    ? {
        patch: (i: number, p: Partial<Step>) =>
          onChange(steps.map((s, j) => (j === i ? { ...s, ...p } : s))),
        move: (i: number, d: -1 | 1) => {
          const next = [...steps];
          const [s] = next.splice(i, 1);
          next.splice(i + d, 0, s!);
          onChange(next);
        },
        remove: (i: number) => onChange(steps.filter((_, j) => j !== i)),
        insert: (i: number, tone: TextTone) => {
          const next = [...steps];
          next.splice(i, 0, textBlock(crypto.randomUUID(), tone));
          onChange(next);
        },
      }
    : null;
  const insertBar = (i: number, last = false) =>
    edit ? (
      <InsertBar
        key={'ins-' + i}
        last={last}
        locale={locale}
        onInsert={(tone) => edit.insert(i, tone)}
      />
    ) : null;

  return (
    <ol className="flex flex-col gap-4">
      {steps.flatMap((step, i) => {
        const tools = edit && (
          <div className="no-print flex shrink-0 items-center opacity-60 transition group-hover:opacity-100 focus-within:opacity-100">
            {canZoom(step) && (
              <IconButton
                label={step.zoom !== false ? t.showWhole : t.zoomClick}
                className={cx(step.zoom !== false && 'text-accent-text')}
                onClick={() => edit.patch(i, { zoom: step.zoom === false })}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="6" />
                  <path d="m20 20-4.5-4.5M11 8v6M8 11h6" />
                </svg>
              </IconButton>
            )}
            {step.imageId && onEditImage && (
              <IconButton
                label={t.editImage(numbers.get(step.id))}
                onClick={() => onEditImage(step)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" />
                </svg>
              </IconButton>
            )}
            <IconButton
              label={t.moveUp(numbers.get(step.id))}
              disabled={i === 0}
              onClick={() => edit.move(i, -1)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="m6 14 6-6 6 6" />
              </svg>
            </IconButton>
            <IconButton
              label={t.moveDown(numbers.get(step.id))}
              disabled={i === steps.length - 1}
              onClick={() => edit.move(i, 1)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="m6 10 6 6 6-6" />
              </svg>
            </IconButton>
            <IconButton label={t.deleteItem(numbers.get(step.id))} onClick={() => edit.remove(i)}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" />
              </svg>
            </IconButton>
          </div>
        );

        if (step.kind === 'text') {
          const tone: TextTone = step.tone || 'info';
          return [
            insertBar(i),
            <li
              key={step.id}
              className={cx('group rounded-[14px] px-4 py-3 sm:px-5', TONE_STYLE[tone])}
              data-block={tone}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  {tone !== 'heading' && (
                    <span className="text-[12px] font-semibold uppercase tracking-wide text-text-2">
                      {textToneLabel(tone, locale)}
                    </span>
                  )}
                  {edit ? (
                    <>
                      <input
                        aria-label={t.blockTitle}
                        className={cx(
                          'block w-full bg-transparent outline-none',
                          tone === 'heading'
                            ? 'text-[20px] font-semibold'
                            : 'text-[15.5px] font-medium',
                        )}
                        placeholder={textToneLabel(tone, locale)}
                        value={step.title}
                        onChange={(e) => edit.patch(i, { title: e.target.value })}
                      />
                      <textarea
                        aria-label={t.blockText}
                        rows={Math.max(1, step.description.split('\n').length)}
                        className="mt-1 block w-full resize-none bg-transparent text-[15px] leading-relaxed text-text-2 outline-none"
                        placeholder={t.textOptional}
                        value={step.description}
                        onChange={(e) => edit.patch(i, { description: e.target.value })}
                      />
                    </>
                  ) : (
                    <>
                      {step.title && (
                        <p
                          className={cx(
                            tone === 'heading'
                              ? 'text-[20px] font-semibold'
                              : 'text-[15.5px] font-medium',
                          )}
                        >
                          {step.title}
                        </p>
                      )}
                      {step.description && (
                        <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">
                          {step.description}
                        </p>
                      )}
                    </>
                  )}
                </div>
                {tools}
              </div>
            </li>,
          ];
        }

        const n = numbers.get(step.id)!;
        return [
          insertBar(i),
          <li
            key={step.id}
            className="group break-inside-avoid rounded-[16px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)] sm:p-5"
            data-step={n}
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[14px] font-semibold text-accent-text">
                {n}
              </span>
              <div className="min-w-0 flex-1">
                {edit ? (
                  <input
                    aria-label={t.stepTitle(n)}
                    className="block w-full bg-transparent text-[17px] font-medium leading-snug outline-none placeholder:text-text-1"
                    placeholder={autoTitle(step, locale)}
                    value={step.title}
                    onChange={(e) => edit.patch(i, { title: e.target.value })}
                  />
                ) : (
                  <p className="text-[17px] font-medium leading-snug">{stepTitle(step, locale)}</p>
                )}
                {(step.kind === 'navigate' || step.kind === 'tab') && step.url && (
                  <a
                    className="mt-0.5 block truncate text-[13.5px] text-accent-text underline-offset-2 hover:underline"
                    href={step.url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {displayUrl(step.url)}
                  </a>
                )}
                {step.kind === 'app' && step.app && (
                  <span
                    className="mt-1 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-text-2"
                    data-step-app={step.app.bundleId}
                  >
                    <SiteIcon site={apps.get(step.app.bundleId) ?? { name: step.app.name }} />
                    {apps.get(step.app.bundleId)?.name ?? step.app.name}
                  </span>
                )}
                {edit ? (
                  <textarea
                    aria-label={t.stepNote(n)}
                    rows={Math.max(1, step.description.split('\n').length)}
                    className="mt-1 block w-full resize-none bg-transparent text-[15px] leading-relaxed text-text-2 outline-none placeholder:text-text-3"
                    placeholder={t.notePlaceholder}
                    value={step.description}
                    onChange={(e) => edit.patch(i, { description: e.target.value })}
                  />
                ) : (
                  step.description && (
                    <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">
                      {step.description}
                    </p>
                  )
                )}
                {edit && <RunControls step={step} onChange={(p) => edit.patch(i, p)} />}
              </div>
              {tools}
            </div>
            {step.imageId && <div className="mt-4">{shot(step, step.zoom !== false)}</div>}
            {below?.(step)}
          </li>,
        ];
      })}
      {insertBar(steps.length, true)}
    </ol>
  );
}

/** Entre os passos: «+» para inserir uma dica, um aviso, uma nota ou uma secção. */
function InsertBar({
  onInsert,
  locale,
  last = false,
}: {
  onInsert: (tone: TextTone) => void;
  locale: Locale;
  last?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const t = guideUiText(useLocale());
  return (
    <li
      className={cx(
        'no-print group/ins relative flex list-none items-center justify-center',
        last ? 'h-10' : '-my-3 h-5',
      )}
      data-insert
    >
      <div className="absolute inset-x-6 h-0.5 rounded-full bg-transparent transition group-hover/ins:bg-border" />
      {open ? (
        <div
          className="relative z-10 flex gap-1 rounded-full bg-surface-1 p-1 shadow-lg shadow-black/10 ring-1 ring-border"
          role="menu"
          aria-label={t.insertBlock}
        >
          {TEXT_TONE_KEYS.map((t) => (
            <button
              key={t}
              type="button"
              role="menuitem"
              className="rounded-full px-3 py-1 text-[13px] font-medium hover:bg-surface-2"
              onClick={() => {
                setOpen(false);
                onInsert(t);
              }}
            >
              {textToneLabel(t, locale)}
            </button>
          ))}
          <button
            type="button"
            className="rounded-full px-2 text-text-3 hover:text-text-1"
            aria-label={t.close}
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-label={t.insertHere}
          title={t.insertTip}
          className={cx(
            'relative z-10 inline-flex size-7 items-center justify-center rounded-full bg-surface-1 text-text-2 shadow-[inset_0_0_0_1px_var(--border-strong)] transition hover:text-text-1 focus-visible:opacity-100',
            last ? 'opacity-100' : 'opacity-0 group-hover/ins:opacity-100',
          )}
          onClick={() => setOpen(true)}
        >
          +
        </button>
      )}
    </li>
  );
}
